import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateAppToken } from '../../../../store/AppTokenAction';
import Loading from '../../../misc/Loading';
import * as AppTokenUtil from '../../../../util/AppTokenUtil';
import { Button, Form, FormFeedback, Input, Label } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CoreAppToken, CoreAppTokenValidation } from '../../../../models/CoreAppToken';
import StatusCode from '../../../../util/StatusCode';
import { RequestState } from '../../../../models/RequestState';
import Copy from '../../../misc/Copy';
import { OperationResult } from '../../../../models/OperationResult';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../../store/store';



interface AppTokenMicrosoftApplicationProps {
    appToken: CoreAppToken,
    disableInput: boolean
}

const verifyTenantId = async (tenantId: string): Promise<OperationResult<string>> => {
    if (tenantId === undefined || tenantId === '') {
        return { ok: false, error: { name: "tenantIdMissing", message: 'Tenant ID eller domän måste anges' } };
    }

    const res = await fetch(`https://login.microsoftonline.com/${tenantId}/v2.0/.well-known/openid-configuration`);
    const wellKnownInfo = await res.json();

    const tokenEndpoint = wellKnownInfo?.token_endpoint;
    if (tokenEndpoint === undefined) {
        return { ok: false, error: { name: "noTokenEndpoint", message: 'Kunde inte verifiera Azure-miljöns Tenant ID eller domän' } };
    }

    const verifiedTenantId = tokenEndpoint.replace(/.*\/([^-]{8}-[^-]{4}-[^-]{4}-[^-]{4}-[^-]{12})\/.*/, "$1");

    return { ok: true, value: verifiedTenantId };
}

const AppTokenMicrosoftApplication = (props: AppTokenMicrosoftApplicationProps) => {
    const appTokenValidationRequestState = useSelector<RootState, RequestState<CoreAppTokenValidation>>(x => x.token.appTokenValidation);
    const dispatch = useDispatch();
    const [tenantId, setTenantId] = useState(props.appToken.tenant ?? '');
    const [tenantIdError, setTenantIdError] = useState<string | undefined>(undefined);
    const [updatingApptoken, setUpdatingAppToken] = useState(false);
    const [verifiedTenantId, setVerifiedTenantId] = useState(false);

    const { t } = useTranslation('configuration');

    useEffect(() => {
        if (!props.appToken.tenant) {
            return;
        }

        verifyTenantId(props.appToken.tenant)
            .then(verifiedTenantId => {
                if (verifiedTenantId.ok) {
                    setVerifiedTenantId(true);
                }
                else {
                    setVerifiedTenantId(false);
                }
            });
    }, [props.appToken.tenant])

    const login = async (appTokenValidate: CoreAppTokenValidation) => {
        let uri = appTokenValidate.flowSyncUXUri;
        let login = await AppTokenUtil.authWindow(uri ?? '');

        if (login.adminConsent && login.tenant) {
            await generateMicrosoftApplication(props.appToken.id, login.tenant);
        } 
    }

    const generateMicrosoftApplication = async (appTokenId: string, tenant: string) => {
        let appTokenBody = {
            appRegistration: props.appToken.appRegistration,
            tenant: tenant
        }

        await updateAppToken(appTokenId, appTokenBody)(dispatch);
    }

    const submitTenantId: React.FormEventHandler<HTMLFormElement> = async (e) => {
        e.preventDefault();

        setUpdatingAppToken(true);
        const verifiedTenantId = await verifyTenantId(tenantId);

        if (verifiedTenantId.ok) {
            await generateMicrosoftApplication(props.appToken.id, verifiedTenantId.value);
        }
        else {
            setTenantIdError(verifiedTenantId.error.name);
        }

        setUpdatingAppToken(false);
    }

    const inputTenantId: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        setVerifiedTenantId(false);
        setTenantId(e.target.value.trim());
    }

    if (appTokenValidationRequestState.code === StatusCode.NONE || appTokenValidationRequestState.code === StatusCode.PENDING) {
        return (<Loading />);
    }

    if (appTokenValidationRequestState.code === StatusCode.ERROR) {
        return (
            <div>
                <p>{t('appTokenConfiguration.validationError.failed')}</p>
                <p>{t('appTokenConfiguration.validationError.response', { code: appTokenValidationRequestState.error.code })}</p>
            </div>)
    }

    const appTokenValidation = appTokenValidationRequestState.data;

    if (!appTokenValidation.valid) {
        return (
            <div>
                <p><FontAwesomeIcon icon="triangle-exclamation" className="apptoken-configuration__status-icon--warning" />{t('appTokenConfiguration.configurationRequired')}</p>
                <div>
                    <strong>{t('appTokenConfiguration.microsoftApplication.option1')}</strong>
                    <div className="apptoken-configuration__button-container">
                        <Button color="primary" disabled={props.disableInput} onClick={() => login(appTokenValidation)}>{t('appTokenConfiguration.microsoftApplication.loginAndAccept')}</Button>
                    </div>
                </div>
                <div className="push--top">
                    <strong>{t('appTokenConfiguration.microsoftApplication.option2')}</strong>
                    <div>
                        <Form onSubmit={submitTenantId}>
                            <div className="push--top">
                                <Label for="userName">{t('appTokenConfiguration.microsoftApplication.option2Step1')}: </Label>
                                <Input disabled={props.disableInput} onChange={inputTenantId} value={tenantId} invalid={tenantIdError !== undefined} valid={verifiedTenantId} />
                                <FormFeedback>{t(`appTokenConfiguration.microsoftApplication.tenantIdError.${tenantIdError}`)}</FormFeedback>
                            </div>
                            <div className="push--top">
                                <Label for="copyLinkUri">{t('appTokenConfiguration.microsoftApplication.option2Step2')}: <Copy value={appTokenValidation.copyLinkUri ?? ''} /></Label>
                                <Input name="copyLinkUri" disabled value={appTokenValidation.copyLinkUri} />
                            </div>
                            <div className="apptoken-configuration__button-container">
                                <Button color="primary" disabled={props.disableInput} type="submit">{t('appTokenConfiguration.saveAndValidate')}</Button>
                                {updatingApptoken && <Loading className="push--left" />}
                            </div>
                        </Form>
                    </div>
                </div>
            </div>)
    }

    return (<div>
        <div className='apptoken-configuration__status'>
            <FontAwesomeIcon icon="check" className="apptoken-configuration__status-icon--ok" />{t('appTokenConfiguration.accessConfigured')}
        </div>
        <div className='apptoken-configuration__status'>
            <FontAwesomeIcon icon="school" className="apptoken-configuration__status-icon" />{t('appTokenConfiguration.microsoftApplication.tenantId')}: {props.appToken.tenant}
        </div>
    </div>);
}

export default AppTokenMicrosoftApplication;